/* eslint-disable camelcase */
import { Grid, Stack } from '@mui/material'
import { FILLING_PERIOD, PREL_AGRI, USAGES } from 'agriAdministration/constants/AgriConstants'
import Checkbox from 'components/forms/Checkbox'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Textarea from 'components/forms/Textarea'
import DtoInstallationTank from 'installation/dto/installation/DtoInstallationTank'
import { orderBy, omitBy } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { hasValue } from 'utils/NumberUtil'
import { formatMilliers } from 'utils/StringUtil'

const StationTankPanel = ({
    tank,
    onChange,
    disabled,
    lockId,
    readMode,
}) => {
    const {
        sandreCodes,
        agriTanksTypes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        agriTanksTypes: store.AgriReducer.agriTanksTypes,
    }), shallowEqual)

    const onChangeValue = (obj) => onChange({ ...tank, ...obj })

    const getStyle = () => {
        return {
            fontSize: '1rem',
            color: disabled ? '#9e9e9e' : '#161832',
        }
    }

    const getSelectedPeriod = (idPeriode, tankPeriodes) => {
        return !!tankPeriodes.find((p) => p.idNbPeriode === idPeriode)
    }

    const handleChangePeriode = (select, code) => {
        if (select) {
            onChangeValue({
                link_periodes: [{
                    idTank: tank.idTank || -1,
                    idPeriode: code,
                    idNbPeriode: null,
                }],
            })
        } else {
            const tankPeriodes = tank.link_periodes.find((p) => p.idNbPeriode === code) ?
                omitBy(tank, ({ idNbPeriode }) => idNbPeriode === null && idNbPeriode === code)
                :
                [
                    ...tank.link_periodes,
                    {
                        idTank: tank.idTank || -1,
                        idPeriode: 2,
                        idNbPeriode: code,
                    },
                ]
            if (!tankPeriodes.length) {
                onChangeValue({
                    link_periodes: [{
                        idTank: tank.idTank || -1,
                        idPeriode: 2,
                        idNbPeriode: null,
                    }],
                })
            } else {
                onChangeValue({ link_periodes: tankPeriodes })
            }
        }
    }

    const periodes = sandreCodes.filter((code) => code.field === USAGES.PERIODES_DETAILS)
    const fillingMode = orderBy(sandreCodes.filter((c) => c.field === PREL_AGRI.MODE_REMPLISSAGE), ['name'])
    const fillingPeriods = sandreCodes.filter((c) => c.field === PREL_AGRI.PERIODE_REMPLISSAGE)
    const saisonnal = (tank.link_periodes || []).find(({ idPeriode }) => idPeriode === FILLING_PERIOD.SEASONAL)
    const annuel = (tank.link_periodes || []).find(({ idPeriode }) => idPeriode === FILLING_PERIOD.ANNUAL)
    const unknown = (tank.link_periodes || []).find(({ idPeriode }) => idPeriode === FILLING_PERIOD.UNKNOWN)
    const fillingPeriod = saisonnal ?
        FILLING_PERIOD.SEASONAL
        :
        (annuel ?
            FILLING_PERIOD.ANNUAL
            :
            (hasValue(unknown) ?
                FILLING_PERIOD.UNKNOWN
                :
                null
            )
        )

    return (
        <Grid container size={12} spacing={1}>
            <Grid size={6}>
                <Input
                    title={i18n.login}
                    value={tank.id}
                    onChange={value => onChangeValue({ id: value })}
                    disabled={disabled || lockId}
                    readMode={readMode}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.detentionType}
                    value={tank.tankType}
                    options={agriTanksTypes}
                    keyValue='id'
                    keyMabel='name'
                    styleLabel={getStyle()}
                    onChange={value => onChangeValue({ tankType: hasValue(value) ? value : null })}
                    disabled={disabled}
                    readMode={readMode}
                    integerValue
                />
            </Grid>
            <Grid size={6}>
                {disabled ? (
                    <Input title={i18n.capability} value={formatMilliers(tank.capacity)} disabled />
                ) : (
                    <NumberField
                        title={i18n.capability}
                        value={tank.capacity}
                        onChange={value => onChangeValue({ capacity: value })}
                        disabled={disabled}
                        readMode={readMode}
                    />
                )}
            </Grid>
            <Grid size={6}>
                <NumberField
                    title={i18n.recoveryFlow}
                    value={tank.recoveryFlow}
                    onChange={value => onChangeValue({ recoveryFlow: value })}
                    floatValue
                    disabled={disabled}
                    readMode={readMode}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.fillingMode}
                    value={tank.fillingMode}
                    options={fillingMode}
                    keyValue='code'
                    styleLabel={getStyle(disabled)}
                    onChange={value => onChangeValue({ fillingMode: hasValue(value) ? value : null })}
                    disabled={disabled}
                    readMode={readMode}
                    integerValue
                    noSort
                />
            </Grid>
            <Grid size={6}>
                <NumberField
                    title={i18n.reserveFlow}
                    value={tank.reserveFlow}
                    onChange={value => onChangeValue({ reserveFlow: value })}
                    floatValue
                    disabled={disabled}
                    readMode={readMode}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.fillingPeriod}
                    value={fillingPeriod}
                    options={fillingPeriods}
                    keyvalue='code'
                    styleLabel={getStyle(disabled)}
                    onChange={(value) => handleChangePeriode(true, value)}
                    disabled={disabled}
                    readMode={readMode}
                />
            </Grid>
            <Grid size={6}>
                <NumberField
                    title={i18n.usedPercentage}
                    value={tank.usedPercentage}
                    onChange={value => onChangeValue({ usedPercentage: value })}
                    disabled={disabled}
                    readMode={readMode}
                    min={0}
                    max={100}
                />
            </Grid>
            <Grid size={6}>
                <Stack direction={'row'} spacing={1}>
                    {saisonnal && periodes.map((p) => (
                        <Checkbox
                            key={p.code}
                            col
                            label={p.name}
                            checked={getSelectedPeriod(p.code, tank.link_periodes)}
                            onChange={() => handleChangePeriode(false, parseInt(p.code))}
                            disabled={readMode || disabled}
                        />
                    ))}
                </Stack>
            </Grid>
            <Grid size={6} />
            <Grid size={4}>
                <SimpleDatePicker
                    label={i18n.startDate}
                    value={tank.affectationStartDate}
                    onChange={v => onChangeValue({ affectationStartDate: v })}
                    disabled={readMode || disabled}
                    max={tank.affectationEndDate}
                />
            </Grid>
            <Grid size={4}>
                <SimpleDatePicker
                    label={i18n.endDate}
                    value={tank.affectationEndDate}
                    onChange={v => onChangeValue({ affectationEndDate: v })}
                    disabled={readMode || disabled}
                    min={tank.affectationStartDate}
                />
            </Grid>
            <Grid size={4}>
                <Checkbox
                    checked={tank.sharedRetention}
                    label={i18n.sharedRetention}
                    onChange={v => onChangeValue({ sharedRetention: v })}
                    disabled={readMode || disabled}
                />
            </Grid>
            <Grid size={12}>
                <Textarea
                    title={i18n.comment}
                    value={tank.comment}
                    onChange={value => onChangeValue({ comment: value })}
                    disabled={disabled}
                    readMode={readMode}
                />
            </Grid>
        </Grid>
    )
}

StationTankPanel.propTypes = {
    tank: PropTypes.instanceOf(DtoInstallationTank),
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    readMode: PropTypes.bool,
    lockId: PropTypes.bool,
}


export default StationTankPanel